import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import axios from 'axios';
import PublicationsPage from './PublicationsPage';
import KeywordsetsPage from './KeywordsetsPage';
import SettingsPage from './SettingsPage';
import TestFunctions from './TestFunctions';
import LoginPage from './LoginPage';
import { loginRequest } from './authConfig';
import { UserProvider, useUser } from './UserContext';

const SUPER_USER_EMAIL = process.env.REACT_APP_SUPER_USER_EMAIL;
const ROLE_USER = 'AR-Cablex_SIMAP_Webapp-User_Prod';
const ROLE_ADMIN = 'AR-Cablex_SIMAP_Webapp-Admin_Prod';
const ROLE_PREVIEW_STORAGE_KEY = 'simap.rolePreview';
const APP_LOCAL_LOGOUT_KEY = 'simap.localLoggedOut';

function resolveAccess(userInfo, rolePreview) {
  const normalizedUserEmail = String(userInfo?.email || '').trim().toLowerCase();
  const isSuperUser = SUPER_USER_EMAIL && normalizedUserEmail === SUPER_USER_EMAIL.toLowerCase();
  const roleList = Array.isArray(userInfo?.roles) ? userInfo.roles : [];
  const isAdmin = roleList.includes(ROLE_ADMIN);
  const isRegularUser = roleList.includes(ROLE_USER);

  let showPublications = isSuperUser || isAdmin || isRegularUser;
  let showKeywordSets = isSuperUser || isAdmin;
  let showTestFunctions = isSuperUser || isAdmin;
  let showSettings = isSuperUser;

  if (isSuperUser) {
    if (rolePreview === 'user') {
      showPublications = true;
      showKeywordSets = false;
      showTestFunctions = false;
      showSettings = false;
    } else if (rolePreview === 'admin') {
      showPublications = true;
      showKeywordSets = true;
      showTestFunctions = true;
      showSettings = false;
    } else if (rolePreview === 'super') {
      showPublications = true;
      showKeywordSets = true;
      showTestFunctions = true;
      showSettings = true;
    }
  }

  return {
    isSuperUser,
    showPublications,
    showKeywordSets,
    showTestFunctions,
    showSettings,
  };
}

function Navigation({
  onSignOut,
  isSuperUser,
  rolePreview,
  onRolePreviewChange,
  showPublications,
  showSettings,
  showTestFunctions,
}) {
  const location = useLocation();
  const { accounts } = useMsal();

  const handleLogout = () => {
    onSignOut();
  };

  const userName = accounts[0]?.name || accounts[0]?.username || '';

  const navStyle = {
    display: 'flex',
    backgroundColor: '#f5f5f5',
    borderBottom: '1px solid #ddd',
    padding: 0,
    margin: 0,
    listStyle: 'none'
  };

  const tabStyle = {
    padding: '12px 24px',
    textDecoration: 'none',
    color: '#333',
    borderRight: '1px solid #ddd',
    display: 'block',
    transition: 'background-color 0.2s'
  };

  const activeTabStyle = {
    ...tabStyle,
    backgroundColor: '#007acc',
    color: 'white'
  };

  return (
    <nav style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #ddd', backgroundColor: '#f5f5f5' }}>
      <ul style={navStyle}>
        {showPublications && (
          <li>
            <Link 
              to="/" 
              style={location.pathname === '/' ? activeTabStyle : tabStyle}
            >
              Publications
            </Link>
          </li>
        )}
        {showSettings && (
          <li>
            <Link 
              to="/keywordsets" 
              style={location.pathname === '/keywordsets' ? activeTabStyle : tabStyle}
            >
              Settings
            </Link>
          </li>
        )}
        {showTestFunctions && (
          <li>
            <Link 
              to="/test-functions" 
              style={location.pathname === '/test-functions' ? activeTabStyle : tabStyle}
            >
              Test Functions
            </Link>
          </li>
        )}
      </ul>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', paddingRight: '16px' }}>
        {isSuperUser && (
          <label style={{ fontSize: '13px', color: '#555', display: 'flex', alignItems: 'center', gap: '6px' }}>
            View as
            <select value={rolePreview} onChange={onRolePreviewChange}>
              <option value="auto">Auto</option>
              <option value="user">User</option>
              <option value="admin">Admin</option>
              <option value="super">Super</option>
            </select>
          </label>
        )}
        <span style={{ fontSize: '14px', color: '#555' }}>{userName}</span>
        <button
          onClick={handleLogout}
          style={{ padding: '6px 16px', fontSize: '13px', cursor: 'pointer', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: 'white' }}
        >
          Sign out
        </button>
      </div>
    </nav>
  );
}

function AppShell({ onSignOut }) {
  const { userInfo, loading, error, reload } = useUser();
  const [rolePreview, setRolePreview] = useState('auto');

  useEffect(() => {
    const persistedRolePreview = localStorage.getItem(ROLE_PREVIEW_STORAGE_KEY);
    if (persistedRolePreview) {
      setRolePreview(persistedRolePreview);
    }
  }, []);

  const handleRolePreviewChange = (event) => {
    const nextRolePreview = event.target.value;
    setRolePreview(nextRolePreview);
    localStorage.setItem(ROLE_PREVIEW_STORAGE_KEY, nextRolePreview);
  };

  const {
    isSuperUser,
    showPublications,
    showKeywordSets,
    showTestFunctions,
    showSettings,
  } = resolveAccess(userInfo, rolePreview);
  const hasBackendCapabilityFlag = typeof userInfo?.canManagePublicationAssignments === 'boolean';
  const canManagePublicationAssignments = isSuperUser
    ? showKeywordSets
    : (hasBackendCapabilityFlag ? userInfo.canManagePublicationAssignments : showKeywordSets);

  const firstAllowedPath = showPublications
    ? '/'
    : (showSettings ? '/keywordsets' : (showTestFunctions ? '/test-functions' : '/'));

  const noAccessElement = (
    <div style={{ padding: '24px' }}>
      No page access granted for this account.
    </div>
  );

  if (loading) {
    return <div style={{ minHeight: '100vh', backgroundColor: '#fff' }} />;
  }

  if (error && !userInfo) {
    return (
      <div style={{ padding: '24px' }}>
        <p>Could not load your account information.</p>
        <button onClick={reload}>Retry</button>{' '}
        <button onClick={onSignOut}>Sign out</button>
      </div>
    );
  }

  return (
    <Router>
      <div>
        <Navigation
          onSignOut={onSignOut}
          isSuperUser={isSuperUser}
          rolePreview={rolePreview}
          onRolePreviewChange={handleRolePreviewChange}
          showPublications={showPublications}
          showSettings={showSettings}
          showTestFunctions={showTestFunctions}
        />
        <Routes>
          <Route
            path="/"
            element={
              showPublications ? (
                <PublicationsPage
                  canManageAssignments={showKeywordSets && canManagePublicationAssignments}
                />
              ) : noAccessElement
            }
          />
          {/* <Route path="/details/:id" element={<DetailsPage />} /> */}
          <Route
            path="/keywordsets"
            element={showSettings ? <KeywordsetsPage /> : <Navigate to={firstAllowedPath} replace />}
          />
          <Route
            path="/test-functions"
            element={showTestFunctions ? <TestFunctions /> : <Navigate to={firstAllowedPath} replace />}
          />
          <Route path="*" element={<Navigate to={firstAllowedPath} replace />} />
          {/* Add more routes as needed */}
        </Routes>
      </div>
    </Router>
  );
}

function App() {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [isLocallyLoggedOut, setIsLocallyLoggedOut] = useState(false);

  useEffect(() => {
    setIsLocallyLoggedOut(localStorage.getItem(APP_LOCAL_LOGOUT_KEY) === '1');
  }, []);

  const handleLocalSignOut = () => {
    localStorage.setItem(APP_LOCAL_LOGOUT_KEY, '1');
    setIsLocallyLoggedOut(true);
    instance.setActiveAccount(null);
  };

  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(async (config) => {
      const requestUrl = String(config.url || '');
      if (!requestUrl.includes('/api/')) {
        return config;
      }

      const account = accounts[0] || instance.getActiveAccount() || instance.getAllAccounts()[0];
      if (!account) {
        return config;
      }

      const tokenResponse = await instance.acquireTokenSilent({
        ...loginRequest,
        account,
      });

      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${tokenResponse.idToken}`;
      return config;
    });

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
    };
  }, [instance, accounts]);

  if (isLocallyLoggedOut || !isAuthenticated) {
    return <LoginPage />;
  }

  return (
    <UserProvider>
      <AppShell onSignOut={handleLocalSignOut} />
    </UserProvider>
  );
}

export default App;
